import * as RadixTabs from '@radix-ui/react-tabs';
import { LayoutGroup, motion } from 'motion/react';
import {
  createContext,
  useContext,
  useId,
  useState,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react';

import { cx } from '@/shared/lib/cx';
import { springs } from '@/shared/lib/motion';

import styles from './Tabs.module.scss';

/**
 * Вкладки: переключение видов доски, разделов настроек.
 *
 * Два подвижных элемента, и оба существуют в единственном экземпляре на
 * список: подчёркивание под активной вкладкой и подложка под той, над
 * которой курсор. При смене вкладки подчёркивание переезжает (layout-
 * анимация с резиновой пружиной), а не гаснет в одном месте и зажигается в
 * другом; подложка так же плывёт за курсором между соседними вкладками.
 */
export const Tabs = RadixTabs.Root;
export const TabsContent = RadixTabs.Content;

interface ListContext {
  activeLayoutId: string;
  hoverLayoutId: string;
  hovered: string | null;
  setHovered: (value: string | null) => void;
}

const Ctx = createContext<ListContext | null>(null);

export type TabsListProps = ComponentPropsWithRef<typeof RadixTabs.List>;

export function TabsList({ className, onPointerLeave, ...rest }: TabsListProps) {
  const id = useId();
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <Ctx.Provider
      value={{ activeLayoutId: `${id}-active`, hoverLayoutId: `${id}-hover`, hovered, setHovered }}
    >
      <LayoutGroup id={id}>
        <RadixTabs.List
          className={cx(styles['tabs'], className)}
          onPointerLeave={(event) => {
            setHovered(null);
            onPointerLeave?.(event);
          }}
          {...rest}
        />
      </LayoutGroup>
    </Ctx.Provider>
  );
}

export interface TabsTriggerProps extends ComponentPropsWithRef<typeof RadixTabs.Trigger> {
  value: string;
  /** Иконка перед подписью. */
  icon?: ReactNode;
  /** Счётчик справа от подписи: число задач в виде, число участников. */
  count?: number;
  /**
   * Активность вкладки. Radix знает её сам, но подчёркиванию нужно решить,
   * рисоваться ли внутри этой вкладки, до того как Radix проставит атрибут.
   */
  active: boolean;
}

export function TabsTrigger({
  value,
  icon,
  count,
  active,
  className,
  children,
  onPointerEnter,
  ...rest
}: TabsTriggerProps) {
  const list = useContext(Ctx);

  if (!list) {
    throw new Error('TabsTrigger должен быть внутри TabsList');
  }

  return (
    <RadixTabs.Trigger
      value={value}
      className={cx(styles['tabs__tab'], className)}
      onPointerEnter={(event) => {
        list.setHovered(value);
        onPointerEnter?.(event);
      }}
      {...rest}
    >
      {list.hovered === value && (
        <motion.span
          className={styles['tabs__hover']}
          layoutId={list.hoverLayoutId}
          transition={springs.rubber}
          aria-hidden="true"
        />
      )}
      {icon !== undefined && icon !== null && <span className={styles['tabs__icon']}>{icon}</span>}
      <span className={styles['tabs__label']}>{children}</span>
      {count !== undefined && <span className={styles['tabs__count']}>{count}</span>}
      {active && (
        <motion.span
          className={styles['tabs__indicator']}
          layoutId={list.activeLayoutId}
          transition={springs.rubber}
          aria-hidden="true"
        />
      )}
    </RadixTabs.Trigger>
  );
}
